"use client";

import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import type { Editor } from "@tiptap/react";
import { useEditorBackend } from "@/lib/editor-backend/context";
import { ALLOWED_CHAPTER_IMAGE_MIME_TYPES, MAX_CHAPTER_IMAGE_SIZE, CHAPTER_IMAGE_SIZE_LIMIT_LABEL } from "@/lib/r2/chapter-image-validation";

/**
 * Read an image file's intrinsic pixel dimensions so the figure can reserve
 * layout space (aspect-ratio) and avoid CLS. Returns null when unavailable
 * (e.g. createImageBitmap unsupported) — the figure just falls back to no
 * reserved ratio.
 */
async function readImageSize(file: File): Promise<{
  width: number;
  height: number;
} | null> {
  try {
    if (typeof createImageBitmap !== "function") return null;
    const bitmap = await createImageBitmap(file);
    const size = {
      width: bitmap.width,
      height: bitmap.height
    };
    bitmap.close();
    return size.width > 0 && size.height > 0 ? size : null;
  } catch {
    return null;
  }
}

/**
 * Encapsulates the editor's "insert image" flow: pick a file, validate it,
 * upload through the active editor backend (real → R2, demo → in-memory), and
 * insert a `figure` node at the cursor. Keeps the toolbar a pure renderer.
 *
 * Returns the hidden-input ref + change handler the caller wires to an
 * `<input type="file">`, plus `openPicker` for the trigger button.
 */
export function useChapterImageInsert(editor: Editor | null, storyId: string) {
  const backend = useEditorBackend();
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const openPicker = useCallback(() => inputRef.current?.click(), []);
  const handleFile = useCallback(async (file: File) => {
    // Client-side guard mirrors the server validation for fast feedback.
    if (!ALLOWED_CHAPTER_IMAGE_MIME_TYPES.has(file.type)) {
      toast.error("Only JPEG, PNG, WebP, and GIF images are accepted");
      return;
    }
    if (file.size > MAX_CHAPTER_IMAGE_SIZE) {
      toast.error(`Image must be under ${CHAPTER_IMAGE_SIZE_LIMIT_LABEL}`);
      return;
    }
    setIsUploading(true);
    try {
      // Measure intrinsic size locally (off the original file) while
      // the upload runs, so the figure can reserve layout space.
      const [result, size] = await Promise.all([backend.uploadChapterImage(storyId, file), readImageSize(file)]);
      if (result.error || !result.url) {
        toast.error(result.error ?? "Image upload failed");
        return;
      }
      if (!editor) {
        // Editor unmounted mid-upload (e.g. chapter switch). The
        // image is in R2 but can't be inserted — tell the author
        // rather than failing silently.
        toast.error("Image uploaded but couldn't be inserted. Please try again.");
        return;
      }
      // Alt is left empty (decorative default) rather than seeded from
      // the filename — a filename is noise to screen readers. Authors
      // add a caption via the figcaption.
      editor.chain().focus().setFigure({
        src: result.url,
        alt: "",
        naturalWidth: size?.width ?? null,
        naturalHeight: size?.height ?? null
      }).run();
    } catch {
      toast.error("Image upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  }, [backend, storyId, editor]);
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file_0 = e.target.files?.[0];
    if (file_0) handleFile(file_0);
    // Reset so re-selecting the same file fires change again.
    if (inputRef.current) inputRef.current.value = "";
  }, [handleFile]);
  return {
    isUploading,
    inputRef,
    openPicker,
    handleInputChange
  };
}